import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { CancellationBreakdown, CancellationSummary } from './CancellationBreakdown';
import { 
  Star, 
  ArrowDown,
  ArrowUp,
  TwoUsers,
  Calendar,
  Filter,
  Download
} from 'react-iconly';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

/**
 * Admin Cancellation Dashboard
 * Shows platform-wide cancellation analytics and reporting
 */
export function AdminCancellationDashboard() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30-days');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    const loadCancelledOrders = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get cancelled parent orders
        const parentOrdersQuery = query(
          collection(db, 'parent_orders'),
          where('status', '==', 'canceled'),
          orderBy('cancelledAt', 'desc'),
          limit(100)
        );
        
        const parentOrdersSnap = await getDocs(parentOrdersQuery);
        const cancelledOrders = parentOrdersSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setOrders(cancelledOrders);
      } catch (err) {
        console.error('Error loading cancelled orders:', err);
        setError('Failed to load cancellation data');
      } finally {
        setLoading(false);
      }
    };

    loadCancelledOrders();
  }, [currentUser, timeRange]);

  // Calculate platform metrics
  const metrics = React.useMemo(() => {
    const totalCancellations = orders.length;
    const totalGrossAmount = orders.reduce((sum, order) => {
      return sum + (order.breakdown?.grossOrderAmount || 0);
    }, 0);
    const totalVestaFees = orders.reduce((sum, order) => {
      return sum + (order.breakdown?.vestaFee || 0);
    }, 0);
    const totalVestaRefunds = orders.reduce((sum, order) => {
      return sum + (order.breakdown?.vestaFeeRefunded || 0);
    }, 0);
    const netVestaRevenue = totalVestaFees - totalVestaRefunds;
    const totalStripeFees = orders.reduce((sum, order) => {
      return sum + (order.breakdown?.stripeFees || 0);
    }, 0);
    const averageOrderValue = totalCancellations > 0 ? totalGrossAmount / totalCancellations : 0;

    // Cancellation reasons breakdown
    const reasonBreakdown = orders.reduce((acc, order) => {
      const reason = order.cancellationReason || 'No reason provided';
      acc[reason] = (acc[reason] || 0) + 1;
      return acc;
    }, {});

    return {
      totalCancellations,
      totalGrossAmount,
      totalVestaFees,
      totalVestaRefunds,
      netVestaRevenue,
      totalStripeFees,
      averageOrderValue,
      reasonBreakdown
    };
  }, [orders]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatOrderNumber = (orderId) => {
    if (!orderId) return 'Order #000000';
    const shortId = orderId.slice(-6).toUpperCase();
    return `Order #${shortId}`;
  };

  const handleViewBreakdown = (order) => {
    setSelectedOrder(order);
    setShowBreakdown(true);
  };

  const exportData = () => {
    const csvData = orders.map(order => ({
      'Order ID': order.id,
      'Customer ID': order.buyerId,
      'Cancelled At': formatDate(order.cancelledAt),
      'Cancelled By': order.cancelledBy || 'Unknown',
      'Reason': order.cancellationReason || 'No reason',
      'Gross Amount': order.breakdown?.grossOrderAmount || 0,
      'Vesta Fee': order.breakdown?.vestaFee || 0,
      'Vesta Refunded': order.breakdown?.vestaFeeRefunded || 0,
      'Stripe Fees': order.breakdown?.stripeFees || 0,
      'Net Vesta Revenue': (order.breakdown?.vestaFee || 0) - (order.breakdown?.vestaFeeRefunded || 0)
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cancellation-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: ACCENT_GRAY }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Loading cancellation data...</div>
          <div style={{ fontSize: 14 }}>Please wait</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: ERROR_RED }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Error</div>
          <div style={{ fontSize: 14 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG }}>
      {/* Header */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: NEUTRAL_WHITE,
        borderBottom: '1px solid #eee',
        padding: '12px 0'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0 20px' 
        }}>
          <h1 style={{
            ...headingFont,
            fontSize: 22,
            color: NEUTRAL_TEXT,
            margin: 0
          }}>
            Platform Cancellation Report
          </h1>
          <button
            onClick={exportData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: PRIMARY_BROWN,
              color: NEUTRAL_WHITE,
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              ...bodyFont
            }}
          >
            <Download set="regular" size={16} />
            Export CSV
          </button>
        </div>
      </header>

      <div style={{ padding: '20px', maxWidth: 1200, margin: '0 auto' }}>
        {/* Metrics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 24
        }}>
          {/* Total Cancellations */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <ArrowDown set="regular" size={24} primaryColor={ERROR_RED} />
              <span style={{
                ...headingFont,
                fontSize: 24,
                color: ERROR_RED
              }}>
                {metrics.totalCancellations}
              </span>
            </div>
            <div style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              Total Cancellations
            </div>
          </div>

          {/* Net Vesta Revenue */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <Star set="regular" size={24} primaryColor={metrics.netVestaRevenue >= 0 ? SUCCESS_GREEN : ERROR_RED} />
              <span style={{
                ...headingFont,
                fontSize: 24,
                color: metrics.netVestaRevenue >= 0 ? SUCCESS_GREEN : ERROR_RED
              }}>
                ${metrics.netVestaRevenue.toFixed(2)}
              </span>
            </div>
            <div style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              Net Vesta Revenue
            </div>
          </div>

          {/* Total Gross Amount */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <ArrowUp set="regular" size={24} primaryColor={PRIMARY_BROWN} />
              <span style={{
                ...headingFont,
                fontSize: 24,
                color: PRIMARY_BROWN
              }}>
                ${metrics.totalGrossAmount.toFixed(2)}
              </span>
            </div>
            <div style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              Total Gross Amount
            </div>
          </div>

          {/* Average Order Value */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <TwoUsers set="regular" size={24} primaryColor={WARNING_ORANGE} />
              <span style={{
                ...headingFont,
                fontSize: 24,
                color: WARNING_ORANGE
              }}>
                ${metrics.averageOrderValue.toFixed(2)}
              </span>
            </div>
            <div style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              Avg Order Value
            </div>
          </div>
        </div>

        {/* Cancellation Reasons */}
        <div style={{
          background: NEUTRAL_WHITE,
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: 24
        }}>
          <h2 style={{
            ...headingFont,
            fontSize: 18,
            color: NEUTRAL_TEXT,
            margin: '0 0 16px 0'
          }}>
            Cancellation Reasons
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(metrics.reasonBreakdown).map(([reason, count]) => (
              <div key={reason} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid #F0F0F0'
              }}>
                <span style={{ fontSize: 14, color: NEUTRAL_TEXT, ...bodyFont }}>
                  {reason}
                </span>
                <span style={{
                  fontSize: 14,
                  color: PRIMARY_BROWN,
                  fontWeight: 600,
                  ...bodyFont
                }}>
                  {count} ({((count / metrics.totalCancellations) * 100).toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Cancelled Orders List */}
        <div style={{
          background: NEUTRAL_WHITE,
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            ...headingFont,
            fontSize: 18,
            color: NEUTRAL_TEXT,
            margin: '0 0 16px 0'
          }}>
            Recent Cancellations
          </h2>

          {orders.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: ACCENT_GRAY
            }}>
              <ArrowDown set="regular" size={48} primaryColor={ACCENT_GRAY} />
              <div style={{ fontSize: 16, marginTop: 12, ...bodyFont }}>
                No cancelled orders found
              </div>
              <div style={{ fontSize: 14, marginTop: 4, ...bodyFont }}>
                Cancelled orders will appear here
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {orders.slice(0, 20).map((order) => (
                <div
                  key={order.id}
                  style={{
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    padding: 16,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleViewBreakdown(order)}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8
                  }}>
                    <div>
                      <h3 style={{
                        ...headingFont,
                        fontSize: 16,
                        color: NEUTRAL_TEXT,
                        margin: '0 0 4px 0'
                      }}>
                        {formatOrderNumber(order.id)}
                      </h3>
                      <div style={{
                        fontSize: 14,
                        color: ACCENT_GRAY,
                        ...bodyFont
                      }}>
                        Customer: {order.buyerId?.slice(-8)} • Cancelled: {formatDate(order.cancelledAt)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: 16,
                        color: NEUTRAL_TEXT,
                        fontWeight: 600,
                        ...bodyFont
                      }}>
                        ${(order.breakdown?.grossOrderAmount || 0).toFixed(2)}
                      </div>
                      <CancellationSummary
                        refundBreakdown={order.breakdown}
                        orderState={order.orderStates?.current || 'canceled'}
                        initiatedBy={order.cancelledBy}
                        viewType="admin"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Breakdown Modal */}
      {showBreakdown && selectedOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 24,
            maxWidth: 600,
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowBreakdown(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'none',
                border: 'none',
                fontSize: 24,
                color: ACCENT_GRAY,
                cursor: 'pointer'
              }}
            >
              ×
            </button>
            
            <CancellationBreakdown
              refundBreakdown={selectedOrder.breakdown}
              orderState={selectedOrder.orderStates?.current || 'canceled'}
              initiatedBy={selectedOrder.cancelledBy}
              cancelledAt={selectedOrder.cancelledAt}
              cancellationReason={selectedOrder.cancellationReason}
              viewType="admin"
            />
          </div>
        </div>
      )}
    </div>
  );
}
